/**
 * `v-tooltip="'Some text'"` — a minimal shared tooltip primitive.
 *
 * One tooltip node is appended to <body> and reused. Behaviour (delay, and a
 * future enabled/disabled switch) is read from the single `tooltipConfig` object,
 * so adding member-level tooltip preferences later means mutating that object —
 * not editing every call site.
 *
 * This is a *visible* affordance only. It is deliberately `aria-hidden` and does
 * not set `aria-describedby`: every control keeps its own `aria-label`, which
 * stays the durable accessible identity whether or not visible tooltips are on.
 * Not a replacement for a fundamentally unclear control, and not native
 * `title=""` (which cannot carry the future visibility/delay behaviour).
 */

export const tooltipConfig = {
  enabled: true,
  delayMs: 400,
};

let tipEl = null;
let styleEl = null;
let showTimer = null;
let activeEl = null;

function injectStyleOnce() {
  if (styleEl || typeof document === 'undefined') return;
  styleEl = document.createElement('style');
  styleEl.textContent = `
.app-tooltip{position:fixed;z-index:9999;max-width:16rem;padding:.25rem .5rem;
border-radius:.25rem;background:rgb(var(--tooltip-bg, 31 41 55));
color:rgb(var(--tooltip-fg, 249 250 251));font-size:.75rem;
line-height:1rem;white-space:nowrap;pointer-events:none;opacity:0;
transition:opacity .12s ease;}
.app-tooltip[data-show="true"]{opacity:1;}`;
  document.head.appendChild(styleEl);
}

function ensureTipEl() {
  if (tipEl) return tipEl;
  injectStyleOnce();
  tipEl = document.createElement('div');
  tipEl.className = 'app-tooltip';
  tipEl.setAttribute('role', 'tooltip');
  tipEl.setAttribute('aria-hidden', 'true');
  document.body.appendChild(tipEl);
  return tipEl;
}

function positionAbove(el) {
  const tip = ensureTipEl();
  const r = el.getBoundingClientRect();
  const tr = tip.getBoundingClientRect();
  const margin = 6;
  let top = r.top - tr.height - margin;
  if (top < margin) top = r.bottom + margin; // flip below if no room above
  let left = r.left + r.width / 2 - tr.width / 2;
  left = Math.max(margin, Math.min(left, window.innerWidth - tr.width - margin));
  tip.style.top = `${Math.round(top)}px`;
  tip.style.left = `${Math.round(left)}px`;
}

function show(el) {
  const text = el.__tooltipText;
  if (!text) return;
  const tip = ensureTipEl();
  tip.textContent = text;
  tip.dataset.show = 'true';
  positionAbove(el);
  window.addEventListener('scroll', onScrollOrKey, true);
  window.addEventListener('keydown', onScrollOrKey, true);
}

function hide() {
  if (showTimer) {
    clearTimeout(showTimer);
    showTimer = null;
  }
  activeEl = null;
  if (tipEl) tipEl.dataset.show = 'false';
  window.removeEventListener('scroll', onScrollOrKey, true);
  window.removeEventListener('keydown', onScrollOrKey, true);
}

function onScrollOrKey(event) {
  if (event.type === 'keydown' && event.key !== 'Escape') return;
  hide();
}

function onEnter(event) {
  if (!tooltipConfig.enabled) return;
  const el = event.currentTarget;
  activeEl = el;
  if (showTimer) clearTimeout(showTimer);
  showTimer = setTimeout(() => {
    if (activeEl === el) show(el);
  }, tooltipConfig.delayMs);
}

function onLeave(event) {
  if (activeEl === event.currentTarget || event.type === 'click') hide();
}

function attach(el, text) {
  el.__tooltipText = text;
  el.addEventListener('pointerenter', onEnter);
  el.addEventListener('pointerleave', onLeave);
  el.addEventListener('focusin', onEnter);
  el.addEventListener('focusout', onLeave);
  el.addEventListener('click', onLeave);
}

function detach(el) {
  if (activeEl === el) hide();
  el.removeEventListener('pointerenter', onEnter);
  el.removeEventListener('pointerleave', onLeave);
  el.removeEventListener('focusin', onEnter);
  el.removeEventListener('focusout', onLeave);
  el.removeEventListener('click', onLeave);
  delete el.__tooltipText;
}

export default {
  mounted(el, binding) {
    if (binding.value) attach(el, String(binding.value));
  },
  updated(el, binding) {
    const text = binding.value ? String(binding.value) : '';
    if (text && el.__tooltipText === undefined) {
      attach(el, text);
    } else if (text) {
      el.__tooltipText = text;
    } else {
      detach(el);
    }
  },
  beforeUnmount(el) {
    detach(el);
  },
};
